import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SiteService } from '../services/site.service';
import { PlatformService } from '../services/platform.service';
import { environment } from '../../../environments/environment';

// Noisy but unactionable — aborted navigations, network blips.
const IGNORE_PATTERNS = [
  'Http failure during parsing',
  'The operation was aborted',
];

// A lazy chunk failed to load (usually a deploy rotated the chunk map under
// an open tab). Previously IGNORED — which silently left pages half-rendered
// until a human refreshed (the missing Contact/Share tabs, Dave 2026-07-08).
// Now: reload once to pick up the fresh index, and report it either way.
const CHUNK_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Loading chunk',
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
];

/** sessionStorage key holding the epoch-ms of the last chunk-triggered reload. */
export const CHUNK_RELOAD_FLAG = 'lp_chunk_reloaded';
/**
 * Guard window after a chunk-triggered reload. A second chunk failure inside
 * this window means the reload did NOT recover — show the "new version
 * available" banner instead of reloading again (no silent death, no reload
 * loop). Outside the window the reload-once recovery is re-armed, so the NEXT
 * deploy's chunk rotation can auto-heal too — the old boolean flag was never
 * reset, so the second incident in a tab's lifetime died silently.
 */
export const CHUNK_RELOAD_GUARD_MS = 60_000;
/**
 * Window event dispatched when the reload-once recovery has already been
 * spent. App listens and shows the existing stale-chunk banner, whose
 * "Reload now" button does a cache-busting navigation (App.reloadApp).
 */
export const STALE_CHUNK_EVENT = 'lp:stale-chunk';

/**
 * Dedup window: the same error message reports at most once per window, so
 * one failure firing once per marker/row/retry costs a single POST instead
 * of one-per-occurrence (Cleveland map incident, 187 identical
 * `Could not load "marker".` posts in one minute, 2026-09-01 — CloudWatch
 * counted 189 events and raised lp-fe-client-error-spike as if the site were
 * down, for one failure on one page). A few minutes collapses a synchronous
 * storm completely without hiding a message that keeps genuinely recurring
 * across an extended session — long enough to matter, short enough that a
 * real regression is never silent for more than a few minutes.
 */
export const ERROR_REPORT_DEDUP_WINDOW_MS = 5 * 60_000;

/**
 * Hard ceiling on *distinct* messages reported per page load. Dedup alone
 * doesn't stop a storm of differently-worded errors (one per failing node,
 * each with a different id baked into the message) from flooding the
 * endpoint just as badly as 187 identical ones did. 20 distinct genuine
 * failures in one page load is already an outage-grade signal on its own —
 * past that, every extra POST is noise the alarm can't act on any
 * differently than it would on #20, so it's better spent as one capped
 * notice than as an unbounded tail of individual reports.
 */
export const ERROR_REPORT_MAX_PER_LOAD = 20;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // message -> epoch-ms of the last time it was actually reported. Per
  // instance (one per app bootstrap / SSR request), never persisted — a
  // sessionStorage quota failure here must not block error reporting the
  // way it must be tolerated for CHUNK_RELOAD_FLAG.
  private readonly lastReportedAt = new Map<string, number>();
  // Count of DISTINCT messages that have actually been posted this page
  // load (i.e. that passed dedup) — what the ceiling is measured against.
  private reportCount = 0;
  private capped = false;
  // Errors dropped by the ceiling since it tripped. Flushed once via
  // queueMicrotask so a synchronous flood (the 187-in-a-loop case) is fully
  // counted before the one summary line goes out, instead of reporting "1
  // suppressed" and then staying silent about the other 186.
  private suppressedSinceCap = 0;
  private capNoticeScheduled = false;

  // Use Injector to avoid the circular dependency that arises when Angular
  // tries to construct HttpClient + ErrorHandler + HTTP interceptors together.
  constructor(private injector: Injector, private platformService: PlatformService) {}

  handleError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    const stack = error instanceof Error ? error.stack : undefined;

    if (!message || message === 'null') return;

    // Noisy-but-unactionable errors (aborted navigations, network blips) and
    // chunk-load failures (auto-recovered via reload-once below) both used to
    // hit console.error() unconditionally above this point — every one of
    // them fails Lighthouse's "errors-in-console" Best Practices audit even
    // though the app already handles or explicitly ignores them. They're
    // still surfaced via console.warn so DevTools visibility isn't lost.
    if (IGNORE_PATTERNS.some(p => message.includes(p))) {
      console.warn('[LP] Ignored error:', error);
      return;
    }

    if (CHUNK_ERROR_PATTERNS.some(p => message.includes(p))) {
      console.warn('[LP] Chunk load error (auto-recovering):', error);
      this.reportError(`[chunk] ${message}`, stack);
      // Reload-once recovery + the stale-chunk banner are both browser-only
      // (a lazy-chunk 404 can only ever happen after the tab loaded real
      // JS chunks — never during a one-shot SSR render).
      if (this.platformService.isBrowser) {
        try {
          // Flag stores WHEN the last chunk-triggered reload happened (legacy
          // value '1' parses as epoch-ms 1 → outside the window → re-arms).
          const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_FLAG)) || 0;
          if (Date.now() - lastReload > CHUNK_RELOAD_GUARD_MS) {
            // First failure (or long-ago flag): reload once — the fresh,
            // no-cache index.html brings the fresh chunk map.
            sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
            this.reloadPage();
          } else {
            // Second failure within the guard window — the reload didn't
            // recover. Surface the banner rather than dying silently.
            window.dispatchEvent(new CustomEvent(STALE_CHUNK_EVENT));
          }
        } catch { /* storage unavailable — stay put */ }
      }
      return;
    }

    // Genuinely unhandled — this is the case console.error exists for.
    console.error('[LP] Unhandled error:', error);
    this.reportError(message, stack);
  }

  /** Seam for specs — window.location.reload() would restart the Karma run. */
  protected reloadPage(): void {
    window.location.reload();
  }

  private reportError(message: string, stack?: string): void {
    if (!this.shouldReport(message)) return;
    this.postClientError(message, stack);
  }

  /**
   * The raw POST, with no flood control. Split out of reportError() so the
   * cap notice can reach the backend WITHOUT passing through shouldReport()
   * — the notice is emitted exactly once (capNoticeScheduled guards it), so
   * it cannot itself flood, and routing it through the gate would suppress
   * the one line that explains the silence.
   */
  private postClientError(message: string, stack?: string): void {
    try {
      const http = this.injector.get(HttpClient);
      const siteService = this.injector.get(SiteService);
      const idSite = siteService.getCurrentSiteId() ?? undefined;

      // `window` is not a global under SSR — still report the error (SSR
      // failures deserve visibility too), just without a page URL to attach.
      const url = this.platformService.isBrowser ? window.location.href.slice(0, 500) : '[ssr]';

      http
        .post(`${environment.apiUrl}/log/client-error`, {
          message: message.slice(0, 2000),
          stack: stack?.slice(0, 10000),
          url,
          idSite,
        })
        .subscribe({ error: () => {} });
    } catch {
      // HTTP not yet available (bootstrap error) — fail silently.
    }
  }

  /**
   * Flood control, applied before any POST — this is what the Cleveland map
   * incident exposed as missing: 187 identical `Could not load "marker".`
   * reports, one per pin, in one minute (see the header comment for
   * ERROR_REPORT_DEDUP_WINDOW_MS). Two independent limits:
   *
   *  1. Dedup: the same message reports at most once per
   *     ERROR_REPORT_DEDUP_WINDOW_MS. A genuinely NEW message is never
   *     delayed by this — it's keyed by message text, so the first
   *     occurrence of any distinct error always passes straight through.
   *  2. Ceiling: once ERROR_REPORT_MAX_PER_LOAD distinct messages have been
   *     reported this page load, reporting stops entirely and a single
   *     capped notice is emitted (see scheduleCapNotice) — silence with no
   *     marker at all would hide a real ongoing outage from whoever reads
   *     the logs next.
   */
  private shouldReport(message: string): boolean {
    if (this.capped) {
      this.suppressedSinceCap++;
      this.scheduleCapNotice();
      return false;
    }

    const now = Date.now();
    const last = this.lastReportedAt.get(message);
    if (last !== undefined && now - last < ERROR_REPORT_DEDUP_WINDOW_MS) {
      return false; // same message, still within its window — already reported
    }

    if (this.reportCount >= ERROR_REPORT_MAX_PER_LOAD) {
      this.capped = true;
      this.suppressedSinceCap = 1; // this call is the first one dropped by the ceiling
      this.scheduleCapNotice();
      return false;
    }

    this.lastReportedAt.set(message, now);
    this.reportCount++;
    return true;
  }

  /**
   * Emits exactly one console.warn for the whole capped-out sequence, once —
   * queued via queueMicrotask so a synchronous flood (the 187-in-a-loop
   * incident shape) finishes incrementing suppressedSinceCap before the
   * line goes out, instead of reporting "1 suppressed" and never mentioning
   * the other 186. Guarded by capNoticeScheduled so later, separately-timed
   * suppressions (a slower trickle past the ceiling) don't each schedule
   * their own line — this stays a single notice per page load, not a
   * second flood of "capped" messages replacing the first flood.
   */
  private scheduleCapNotice(): void {
    if (this.capNoticeScheduled) return;
    this.capNoticeScheduled = true;
    queueMicrotask(() => {
      const notice =
        `[LP] Client-error reporting capped at ${ERROR_REPORT_MAX_PER_LOAD} distinct errors ` +
        `for this page load — ${this.suppressedSinceCap} further error report(s) suppressed.`;
      console.warn(notice);
      // A console line only exists in the visitor's own browser. Without this
      // POST the backend sees reporting stop dead and cannot tell a fixed page
      // from a capped one — silence that looks like health is the failure mode
      // this whole guard exists to avoid.
      this.postClientError(notice);
    });
  }
}
